/**
 * Atlas — per-super-admin personal command center.
 *
 * Tabs: List (Kanban) · Cal. (Google Calendar) · Hist. (daily 11PM snapshots)
 *       and a search-only icon pill on the right.
 */
import { useEffect, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { toast } from "sonner";
import { Compass, MagnifyingGlass } from "@phosphor-icons/react";
import AtlasBoard from "@/components/atlas/AtlasBoard";
import AtlasCalendar from "@/components/atlas/AtlasCalendar";
import AtlasHistory from "@/components/atlas/AtlasHistory";
import AtlasSearch from "@/components/atlas/AtlasSearch";
import AtlasVoiceButton from "@/components/atlas/AtlasVoiceButton";

function fmtLU(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const mm = d.getMonth() + 1;
  const dd = d.getDate();
  const yy = String(d.getFullYear()).slice(-2);
  let hh = d.getHours();
  const mi = String(d.getMinutes()).padStart(2, "0");
  const ap = hh >= 12 ? "PM" : "AM";
  hh = hh % 12 || 12;
  return `L.U. ${mm}.${dd}.${yy} ${hh}:${mi}${ap}`;
}

export default function AtlasPage() {
  const [tab, setTab] = useState("list"); // list | calendar | history | search
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadBoard = async () => {
    try {
      const { data } = await api.get("/atlas/board");
      setBoard(data);
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadBoard(); }, []);

  const onVoiceCommitted = () => { loadBoard(); };

  const lu = fmtLU(board?.updated_at);

  return (
    <div className="min-h-screen bg-[#F4F6F8]">
      <AppHeader sticky={false} />
      <main className="max-w-[1600px] mx-auto px-3 sm:px-5 lg:px-8 py-4 sm:py-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-[#1f64c2] text-white flex items-center justify-center shadow-sm">
              <Compass size={18} weight="bold" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900 leading-none">Atlas</h1>
            {lu && (
              <span
                className="text-[10px] sm:text-[11px] font-semibold tracking-wide text-rose-400/90 self-end pb-1"
                data-testid="atlas-last-updated"
                title={board?.updated_at ? new Date(board.updated_at).toLocaleString() : ""}
              >
                {lu}
              </span>
            )}
          </div>

          {/* Pill selector: List | Cal. | Hist. | 🔍 */}
          <div
            className="inline-flex items-center rounded-full bg-white border border-slate-200 p-1 shadow-sm"
            data-testid="atlas-tabs"
          >
            <PillText active={tab === "list"} onClick={() => setTab("list")} testId="atlas-tab-list">List</PillText>
            <PillText active={tab === "calendar"} onClick={() => setTab("calendar")} testId="atlas-tab-calendar">Cal.</PillText>
            <PillText active={tab === "history"} onClick={() => setTab("history")} testId="atlas-tab-history">Hist.</PillText>
            <PillIcon active={tab === "search"} onClick={() => setTab("search")} testId="atlas-tab-search">
              <MagnifyingGlass size={14} weight="bold" />
            </PillIcon>
          </div>
        </div>

        {tab === "list" && (
          <AtlasBoard board={board} loading={loading} onReload={loadBoard} />
        )}
        {tab === "calendar" && <AtlasCalendar />}
        {tab === "history" && <AtlasHistory />}
        {tab === "search" && <AtlasSearch />}
      </main>

      <AtlasVoiceButton onCommitted={onVoiceCommitted} />
    </div>
  );
}

function PillText({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`rounded-full px-3 py-1 text-[13px] font-semibold transition-colors ${
        active ? "bg-[#1f64c2] text-white shadow-sm" : "text-slate-500 hover:text-slate-900"
      }`}
    >
      {children}
    </button>
  );
}

function PillIcon({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`rounded-full px-2 py-1 text-[13px] font-semibold transition-colors ml-0.5 ${
        active ? "bg-[#1f64c2] text-white shadow-sm" : "text-slate-500 hover:text-slate-900"
      }`}
      aria-label="Atlas search"
    >
      {children}
    </button>
  );
}
